import React, { Fragment, ReactNode } from "react";
import Units, { UnitName } from "@hpo/client/utilities/Units";
import { Performance } from "@hpo/client/models/Performance";
import Print from "@hpo/client/utilities/print/Print";

export function ProgressBar(props: { ratio: number | null }) {
  const { ratio } = props;
  if (ratio === null) return null;
  return (
    <div className="progressbar">
      <div
        className={
          ratio >= 1
            ? "progressbar-thumb-full"
            : ratio >= 0.5
              ? "progressbar-thumb-half"
              : "progressbar-thumb-few"
        }
        style={{
          width: Math.round(ratio * 100) + "%",
        }}
      ></div>
    </div>
  );
}

export type PerformanceAndPrevious = {
  current: Performance | null;
  previous: Performance | null;
};

export function getPerformanceAndPrevious(
  perfs: Record<string, Performance>,
  period: string,
  periods: Array<string>,
) {
  const current = period in perfs ? perfs[period] : null;
  const currentPeriodIndex = periods.indexOf(period);
  const prevPeriod =
    currentPeriodIndex > 0 ? periods[currentPeriodIndex - 1] : null;
  const previous =
    prevPeriod === null ? null : prevPeriod in perfs ? perfs[prevPeriod] : null;
  return { current, previous };
}

export function displayPerformance(
  perfs: PerformanceAndPrevious,
  unit: UnitName,
): ReactNode {
  const { current, previous } = perfs;
  let output: ReactNode = null;
  if (current) {
    const { actual, goal, ratio } = current;
    if (actual === null) {
      if (goal === null) {
        output = "-";
      } else {
        output = (
          <Print.OneLine>
            - <small>/{Units[unit].display(goal)}</small>
          </Print.OneLine>
        );
      }
    } else {
      if (goal === null) {
        output = Units[unit].display(actual);
      } else {
        output = (
          <Fragment>
            {Units[unit].display(actual)}
            <small>/{Units[unit].display(goal)}</small>
          </Fragment>
        );
      }
      output = <div>{output}</div>;
    }
    if (ratio !== null) {
      output = (
        <Fragment>
          {output}
          <ProgressBar ratio={ratio} />
        </Fragment>
      );
    }
    if (
      current !== null &&
      current.actual !== null &&
      current.actual !== 0 &&
      previous !== null &&
      previous.actual !== null &&
      previous.actual !== 0
    ) {
      const progress = current.actual / previous.actual - 1;
      output = (
        <Fragment>
          {output}
          <small
            className={[
              "variation",
              switchVariation(progress, "positive", "neutral", "negative"),
            ].join(" ")}
          >
            {switchVariation(progress, "▲", "-", "▼")}
            {Units.percentage.display(progress)}
          </small>
        </Fragment>
      );
    }
  }
  return <div className="performance">{output}</div>;
}

function switchVariation<T>(
  variation: number,
  ifPositive: T,
  ifNeutral: T,
  ifNegative: T,
): T {
  if (variation === 0) return ifNeutral;
  else if (variation > 0) return ifPositive;
  else return ifNegative;
}
